/**
 * @fileoverview Zod schema for TenantDetailModal's "Edit Prospect" form.
 * @description Same required-field rigor as prospect creation — this edits the identical profile
 * fields, just pre-filled. Every issue message is a translation key (per this repo's i18n rule for
 * validators), translated only at the display call site in EditProspectForm.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { z } from 'zod'

import { emailField, mobileNumberField } from '@/shared/lib/validation/contact-fields.schema'

export const editProspectFormSchema = z.object({
  name: z.string().trim().min(1, 'sales.tenantDetail.edit.errors.nameRequired'),
  bankType: z.enum(['Urban Co-operative', 'State Co-operative', 'District Central Co-operative']),
  headOffice: z.string().trim().min(1, 'sales.tenantDetail.edit.errors.headOfficeRequired'),
  regNumber: z.string().trim().optional(),
  website: z.string().trim().optional(),
  state: z.string().trim().min(1, 'sales.tenantDetail.edit.errors.stateRequired'),
  city: z.string().trim().optional(),
  district: z.string().trim().optional(),
  pincode: z.string().trim().optional(),
  address: z.string().trim().optional(),
  branches: z.number().int().min(1, 'sales.tenantDetail.edit.errors.branchesRequired'),
  employees: z.number().int().min(0).optional(),
  coreBanking: z.string().trim().optional(),
  existingMis: z.string().trim().optional(),
  contactName: z.string().trim().min(1, 'sales.tenantDetail.edit.errors.contactNameRequired'),
  contactDesignation: z.string().trim().optional(),
  contactEmail: emailField({
    required: 'sales.tenantDetail.edit.errors.contactEmailRequired',
    invalid: 'sales.tenantDetail.edit.errors.contactEmailInvalid',
  }),
  contactPhone: mobileNumberField(),
  leadSource: z.string().trim().min(1, 'sales.tenantDetail.edit.errors.leadSourceRequired'),
  leadRating: z.enum(['Hot', 'Warm', 'Cold']),
  notes: z.string().trim().optional(),
})

export type EditProspectFormValues = z.infer<typeof editProspectFormSchema>
